import React, { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Calendar, Clock, MapPin, TrendingUp, AlertTriangle, User, FileText,
  KeyRound, Download, Briefcase, CalendarDays, Timer, ShieldAlert, ShieldCheck,
} from 'lucide-react';

export default function MyDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [leaves, setLeaves] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [uploadedPayslips, setUploadedPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [pinForm, setPinForm] = useState({ old_pin: '', new_pin: '', confirm_pin: '' });

  // Filters
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth, dateFrom, dateTo]);

  useEffect(() => {
    fetchLeaves();
    fetchPayslips();
    fetchUploadedPayslips();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/my/profile');
      setProfile(res.data);
    } catch (error) {
      console.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await api.get(`/my/attendance?${params.toString()}`);
      setAttendance(res.data);
    } catch (error) {
      console.error('Failed to fetch attendance');
    }
  };

  const fetchLeaves = async () => {
    try {
      const res = await api.get('/my/leaves');
      setLeaves(res.data);
    } catch (error) {
      console.error('Failed to fetch leaves');
    }
  };

  const fetchPayslips = async () => {
    try {
      const res = await api.get('/my/payslips');
      setPayslips(res.data);
    } catch (error) {
      console.error('Failed to fetch payslips');
    }
  };

  const fetchUploadedPayslips = async () => {
    try {
      const res = await api.get('/my/uploaded-payslips');
      setUploadedPayslips(res.data);
    } catch (error) {
      console.error('Failed to fetch uploaded payslips');
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pinForm.new_pin)) { toast.error('New PIN must be exactly 6 digits'); return; }
    if (pinForm.new_pin !== pinForm.confirm_pin) { toast.error('PINs do not match'); return; }
    try {
      await api.post('/my/change-pin', { old_pin: pinForm.old_pin, new_pin: pinForm.new_pin });
      toast.success('PIN changed successfully!');
      setChangePinOpen(false);
      setPinForm({ old_pin: '', new_pin: '', confirm_pin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change PIN');
    }
  };

  const downloadPayslipPDF = async (payslipId, month) => {
    try {
      const res = await api.get(`/payroll/${payslipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaySlip-${profile?.name?.replace(/ /g, '_') || 'Employee'}-${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch { toast.error('Failed to download'); }
  };

  const downloadUploadedPayslip = async (upload) => {
    try {
      const res = await api.get(`/payslip-uploads/${upload.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaySlip-${profile?.name?.replace(/ /g, '_') || 'Employee'}-${upload.month}.${upload.filename.split('.').pop()}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Failed to download'); }
  };

  const formatTime = (t) => {
    if (!t) return '-';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDuration = (mins) => {
    if (!mins || mins <= 0) return '0min';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}hr ${m}min` : `${m}min`;
  };

  const clearDateFilters = () => {
    setDateFrom('');
    setDateTo('');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-slate-500">Loading...</p></div>;
  }

  const summary = attendance?.summary || {};
  const records = attendance?.records || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-dashboard">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-[#FF9933] via-white to-[#138808] rounded-xl p-[2px]">
        <div className="bg-white rounded-xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#FF9933]/10 flex items-center justify-center shrink-0">
                {profile?.photo_url ? (
                  <img src={profile.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <User className="w-7 h-7 text-[#FF9933]" />
                )}
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-slate-800">{profile?.name || user?.name}</h1>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5" />
                    Code: <span className="font-mono font-bold text-[#FF9933]">{profile?.employee_code}</span>
                  </span>
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Shift: {profile?.shift_start} - {profile?.shift_end}
                  </span>
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => setChangePinOpen(true)} className="shrink-0" data-testid="change-pin-btn">
              <KeyRound className="w-4 h-4 mr-2" /> Change PIN
            </Button>
          </div>
        </div>
      </div>

      {/* Month Selector */}
      <div className="flex items-center gap-3">
        <Label className="text-sm text-slate-600 shrink-0">Month</Label>
        <Input
          type="month"
          value={selectedMonth}
          onChange={(e) => { setSelectedMonth(e.target.value); clearDateFilters(); }}
          className="w-44"
          data-testid="my-month-picker"
        />
      </div>

      {/* Monthly Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="bg-white border shadow-sm" data-testid="my-stat-working">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Working Days</p>
            <p className="text-xl font-bold text-slate-800">{summary.working_days || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm" data-testid="my-stat-present">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Present Days</p>
            <p className="text-xl font-bold text-[#138808]">{summary.total_days || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm" data-testid="my-stat-absent">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Absent Days</p>
            <p className="text-xl font-bold text-red-500">{summary.absent_days || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm" data-testid="my-stat-hours">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Total Hours</p>
            <p className="text-xl font-bold text-slate-800">{summary.total_hours || '0min'}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm" data-testid="my-stat-late">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Late Days</p>
            <p className="text-xl font-bold text-red-500">{summary.late_days || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Leave & Overtime row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-blue-600 mb-1">Paid Leave</p>
            <p className="text-xl font-bold text-blue-700">{summary.paid_leave_days || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <p className="text-xs text-yellow-600 mb-1">Unpaid Leave</p>
            <p className="text-xl font-bold text-yellow-700">{summary.unpaid_leave_days || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-xs text-purple-600 mb-1">Overtime Days</p>
            <p className="text-xl font-bold text-purple-700">{summary.overtime_days || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-xs text-green-600 mb-1">Overtime Hours</p>
            <p className="text-xl font-bold text-green-700">{summary.overtime || '0min'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs — Attendance / Payslips / Leaves */}
      <Tabs defaultValue="attendance" className="w-full">
        <TabsList className="mb-4" data-testid="my-tabs">
          <TabsTrigger value="attendance" className="gap-1.5"><CalendarDays className="w-4 h-4" />Daily Attendance</TabsTrigger>
          <TabsTrigger value="payslips" className="gap-1.5"><FileText className="w-4 h-4" />Payslips</TabsTrigger>
          <TabsTrigger value="leaves" className="gap-1.5"><Timer className="w-4 h-4" />Leave Balance</TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <Card className="bg-white border shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg">Daily Attendance</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-[150px] text-sm"
                    placeholder="From"
                    data-testid="my-date-from"
                  />
                  <span className="text-slate-400 text-sm">to</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-[150px] text-sm"
                    placeholder="To"
                    data-testid="my-date-to"
                  />
                  {(dateFrom || dateTo) && (
                    <Button variant="ghost" size="sm" onClick={clearDateFilters} className="text-xs text-slate-500">
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="my-attendance-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Check In</TableHead>
                      <TableHead className="font-semibold">Check Out</TableHead>
                      <TableHead className="font-semibold">Hours</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold">Range</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          No attendance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map((r) => (
                        <TableRow key={r.id} data-testid={`my-att-row-${r.id}`}>
                          <TableCell className="font-medium text-slate-800">{r.date}</TableCell>
                          <TableCell className="text-green-600 font-medium">{formatTime(r.check_in)}</TableCell>
                          <TableCell className={r.check_out ? 'text-slate-700' : 'text-[#FF9933] font-medium'}>
                            {r.check_out ? formatTime(r.check_out) : 'Active'}
                          </TableCell>
                          <TableCell className="text-slate-700">{r.hours_worked || '-'}</TableCell>
                          <TableCell>
                            {r.is_late ? (
                              <Badge variant="destructive" className="text-xs">
                                Late {r.late_minutes > 0 ? `${Math.floor(r.late_minutes / 60) > 0 ? Math.floor(r.late_minutes / 60) + 'h ' : ''}${r.late_minutes % 60}m` : ''}
                              </Badge>
                            ) : (
                              <Badge className="bg-[#138808] hover:bg-[#107307] text-xs">On Time</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600 flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              {r.check_in_area || r.location_name || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {r.check_in_out_of_range ? (
                              <Badge variant="destructive" className="text-xs flex items-center gap-1 w-fit">
                                <ShieldAlert className="w-3 h-3" />{r.check_in_geofence_distance}m
                              </Badge>
                            ) : r.check_in_geofence_distance != null ? (
                              <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100 flex items-center gap-1 w-fit">
                                <ShieldCheck className="w-3 h-3" />{r.check_in_geofence_distance}m
                              </Badge>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payslips Tab */}
        <TabsContent value="payslips">
          {/* Uploaded Payslips */}
          {uploadedPayslips.length > 0 && (
            <Card className="bg-white border shadow-sm mb-4">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#FF9933]" />
                  Payslip Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {uploadedPayslips.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-3 px-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`my-uploaded-payslip-${u.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${u.content_type === 'application/pdf' ? 'bg-red-50' : 'bg-blue-50'}`}>
                          {u.content_type === 'application/pdf' ? (
                            <FileText className="w-5 h-5 text-red-500" />
                          ) : (
                            <FileText className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u.month}</p>
                          <p className="text-xs text-slate-500">{u.filename}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => downloadUploadedPayslip(u)} data-testid={`my-download-uploaded-${u.id}`}>
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* System-generated Payslips */}
          <Card className="bg-white border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Generated Payslips</CardTitle>
            </CardHeader>
            <CardContent>
              {payslips.length > 0 ? (
                <div className="space-y-3">
                  {payslips.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-3 px-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`my-payslip-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{p.month}</p>
                          <p className="text-xs text-slate-500">
                            Gross: Rs. {p.gross_salary?.toLocaleString()} | Deductions: Rs. {p.total_deductions?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-base font-bold text-green-600">Rs. {p.net_salary?.toLocaleString()}</span>
                        <Button size="sm" variant="outline" onClick={() => downloadPayslipPDF(p.id, p.month)} data-testid={`my-download-payslip-${p.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-8">No generated payslips available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves">
          <Card className="bg-white border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Leave Balance & History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex-1 text-center">
                  <p className="text-xs text-slate-500 mb-1">Paid Leave Balance</p>
                  <p className="text-2xl font-bold text-blue-600">{leaves?.paid_leave_balance ?? 0}</p>
                  <p className="text-xs text-slate-400">days remaining</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="flex-1 text-center">
                  <p className="text-xs text-slate-500 mb-1">Used This Month</p>
                  <p className="text-2xl font-bold text-[#FF9933]">{(summary.paid_leave_days || 0) + (summary.unpaid_leave_days || 0)}</p>
                  <p className="text-xs text-slate-400">days</p>
                </div>
              </div>

              {leaves?.leaves?.length > 0 ? (
                <div className="space-y-2">
                  {leaves.leaves.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2.5 px-3 border rounded-lg" data-testid={`my-leave-${l.id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{l.start_date} to {l.end_date} ({l.days} day{l.days > 1 ? 's' : ''})</p>
                        <p className="text-xs text-slate-500">{l.reason}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={l.leave_type === 'paid' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'}>
                          {l.leave_type}
                        </Badge>
                        <Badge className={
                          l.status === 'approved' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                          l.status === 'rejected' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                          'bg-slate-100 text-slate-600 hover:bg-slate-100'
                        }>
                          {l.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">No leave requests yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Change PIN Dialog */}
      <Dialog open={changePinOpen} onOpenChange={setChangePinOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change PIN</DialogTitle>
            <DialogDescription>Enter your current PIN and choose a new 6-digit PIN</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePin} className="space-y-4">
            <div className="space-y-2">
              <Label>Current PIN</Label>
              <Input type="password" maxLength={6} value={pinForm.old_pin}
                onChange={(e) => setPinForm(p => ({ ...p, old_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="Current 6-digit PIN" required className="font-mono tracking-widest text-center" data-testid="current-pin-input" />
            </div>
            <div className="space-y-2">
              <Label>New PIN</Label>
              <Input type="password" maxLength={6} value={pinForm.new_pin}
                onChange={(e) => setPinForm(p => ({ ...p, new_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="New 6-digit PIN" required className="font-mono tracking-widest text-center" data-testid="new-pin-input" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New PIN</Label>
              <Input type="password" maxLength={6} value={pinForm.confirm_pin}
                onChange={(e) => setPinForm(p => ({ ...p, confirm_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                placeholder="Confirm 6-digit PIN" required className="font-mono tracking-widest text-center" data-testid="confirm-pin-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setChangePinOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="submit-change-pin">Change PIN</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
